import MobileBookingSummary from '@/components/booking-summary/MobileBookingSummary';
import RegularBookingSummary from '@/components/booking-summary/RegularBookingSummary';
import Footer from '@/components/Footer';
import ChangeTripsModal from '@/components/manage/change-trips/ChangeTripsModal';
import MakeChanges from '@/components/manage/make-changes/MakeChanges';
import RefundExtrasModal from '@/components/manage/refund/extras/RefundExtrasModal';
import RefundLegModal from '@/components/manage/refund/leg/RefundLegModal';
import RefundNonTripModal from '@/components/manage/refund/non-trip-offer/RefundNonTripModal';
import RefundOptions, {
  type RefundOptionType,
} from '@/components/manage/refund/RefundOptions';
import Tickets from '@/components/manage/tickets/Tickets';
import TravelPasses from '@/components/manage/travel-passes/TravelPasses';
import {
  clearManageFlowBooking,
  getManageFlowBookingById,
  getManageFlowTravelAccountsMap,
  resetChangeTripsSelection,
} from '@/features/manage/manageActions';
import {
  manageFlowBookingSelector,
  manageFlowTravelAccountsMapSelector,
} from '@/features/manage/manageSelectors';
import { resetPurchase } from '@/features/purchase/purchaseActions';
import { useBookingRefundEligibleExtras } from '@/hooks/useBookingRefundEligibleExtras';
import { useBookingRefundEligibleTrips } from '@/hooks/useBookingRefundEligibleTrips';
import useBookingSummaryData from '@/hooks/useBookingSummaryData';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSeparateNonTripOffersBasedOnType } from '@/hooks/useSeparateNonTripOffersBasedOnType';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import useUsedVouchers from '@/hooks/useUsedVouchers';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import {
  getBookingNonTripOfferFulfillments,
  isBookingRefunded,
  isTravelPassBooking,
} from '@/utils/booking';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { isTimestampInPast } from '@/utils/dateTime';
import {
  Button,
  Divider,
  Icons,
  Spinner,
  Typography,
} from '@packages/webshop-design-system';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';

interface BookingDetails {}

export const BookingDetails: FC<BookingDetails> = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const booking = useSelector(manageFlowBookingSelector);
  const usedVouchers = useUsedVouchers({ bookingId: booking?.id });
  const travelAccountsMap = useSelector(manageFlowTravelAccountsMapSelector);
  const summaryData = useBookingSummaryData({
    booking,
    passengers: booking?.passengers,
    vouchers: usedVouchers.map(({ voucherCode, ...rest }) => ({
      ...rest,
      voucher_code: voucherCode,
    })),
    displayPrice: booking?.confirmedPrice,
    travelAccountsMap,
    // EXCHANGED admissions are deliberately absent: they were superseded by
    // the admission that replaced them, so listing them would double-count
    // the trip in the summary (BR-57809).
    allowedStatuses: ['FULFILLED', 'REFUNDED', 'RELEASED'],
  });
  const [isRefundOptionsOpen, setIsRefundOptionsOpen] = useState(false);
  const [activeRefundModal, setActiveRefundModal] =
    useState<RefundOptionType | null>(null);
  const [isMakeChangesOpen, setIsMakeChangesOpen] = useState(false);
  const [isChangeTripsModalOpen, setIsChangeTripsModalOpen] = useState(false);
  const { refundEligibleOutboundTrips, refundEligibleInboundTrips } =
    useBookingRefundEligibleTrips(booking);
  const { areEligibleExtrasRefundable } =
    useBookingRefundEligibleExtras(booking);
  const { travelPass, multiRide } = useSeparateNonTripOffersBasedOnType(
    booking?.nonTripOffers,
    travelAccountsMap
  );
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const { t } = useTranslation();
  const tenantConfiguration = useTenantConfiguration();
  const areEligibleTripsRefundable = Boolean(
    refundEligibleOutboundTrips.length || refundEligibleInboundTrips.length
  );
  const areEligibleNonTripOffersRefundable = Boolean(
    [...travelPass, ...multiRide].filter(
      ({ status, refundable, validUntil }) =>
        status !== 'REFUNDED' &&
        refundable === 'YES' &&
        (!validUntil || !isTimestampInPast(validUntil, 'Europe/Stockholm'))
    ).length
  );
  const isTripBooking = Boolean(booking?.bookedTrips?.length);

  useEffect(() => {
    if (!location?.state?.reopenChangeTripsModal) {
      return;
    }
    setIsChangeTripsModalOpen(true);
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null }
    );
  }, [location.pathname, location.search, location.state, navigate]);

  // Entering the booking view means any change-trips selection is stale —
  // `manage.changeTrips` is redux-persisted so an abandoned flow (footer
  // Back, nav links, hard reload mid-flow) would otherwise haunt every later
  // visit: the summary flips to change mode (Reusable sum / Change total)
  // and /purchase stays locked to the exchange search (BR-57809). The one
  // legitimate carrier of a selection into this view is the offer-page Back
  // button, which announces itself via `reopenChangeTripsModal` state so the
  // Change-Trips modal can reopen pre-filled — skip the reset for it.
  //
  // `completedChangeFlow` marks arrival from a paid change-flow checkout,
  // which defers its purchase-state cleanup to here: resetting while
  // checkout is still mounted would empty its booking and trip the
  // no-booking redirect to /purchase mid-navigation. Mirrors what /success
  // does for regular purchases.
  //
  // Runs once with the mount-time state; the reopen effect above clears
  // `location.state` on its own schedule, so re-runs must not re-read it.
  const hasClearedStaleChangeStateRef = useRef(false);
  useEffect(() => {
    if (hasClearedStaleChangeStateRef.current) {
      return;
    }
    hasClearedStaleChangeStateRef.current = true;
    if (location?.state?.completedChangeFlow) {
      dispatch(resetPurchase());
      void persistor.purge();
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: null }
      );
    }
    if (!location?.state?.reopenChangeTripsModal) {
      dispatch(resetChangeTripsSelection());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRefundButtonVisible =
    areEligibleTripsRefundable ||
    areEligibleNonTripOffersRefundable ||
    areEligibleExtrasRefundable;

  const refundOptions = useMemo(() => {
    const options: Array<{
      type: RefundOptionType;
      labelKey:
        | 'refundTripsOption'
        | 'refundExtrasOption'
        | 'refundTravelPassesOption';
      icon: ReactNode;
    }> = [];

    if (areEligibleTripsRefundable) {
      options.push({
        type: 'trips',
        labelKey: 'refundTripsOption',
        icon: <Icons.ticketReturn width={20} height={20} />,
      });
    }

    if (areEligibleExtrasRefundable && isTripBooking) {
      options.push({
        type: 'extras',
        labelKey: 'refundExtrasOption',
        icon: <Icons.addon width={20} height={20} />,
      });
    }

    if (areEligibleNonTripOffersRefundable) {
      options.push({
        type: 'travelPasses',
        labelKey: 'refundTravelPassesOption',
        icon: <Icons.ticketReturn width={20} height={20} />,
      });
    }

    return options;
  }, [
    areEligibleExtrasRefundable,
    areEligibleNonTripOffersRefundable,
    areEligibleTripsRefundable,
    isTripBooking,
  ]);

  const handleRefundClick = useCallback(() => {
    if (refundOptions.length === 1) {
      setActiveRefundModal(refundOptions[0].type);
      return;
    }

    setIsRefundOptionsOpen(true);
  }, [refundOptions]);

  const handleRefundOptionClick = useCallback((type: RefundOptionType) => {
    setActiveRefundModal(type);
  }, []);

  const handleRefundModalClose = useCallback(() => {
    setActiveRefundModal(null);
  }, []);

  const allTripLegs = (booking?.bookedTrips ?? []).flatMap(
    (trip) => trip.legs ?? []
  );
  const isBookingPast =
    allTripLegs.length > 0 &&
    allTripLegs.every(
      (leg) =>
        leg.departureTime &&
        isTimestampInPast(leg.departureTime, 'Europe/Stockholm')
    );
  const isMakeChangesVisible =
    isTripBooking &&
    !isBookingRefunded(booking) &&
    !isBookingPast &&
    tenantConfiguration?.showMakeChanges;

  // Determine badge status for travel passes
  const hasRefundedTravelPass = [...travelPass, ...multiRide].some(
    ({ status }) => status === 'REFUNDED'
  );
  const hasExpiredTravelPass = [...travelPass, ...multiRide].some(
    ({ validUntil }) =>
      validUntil && isTimestampInPast(validUntil, 'Europe/Stockholm')
  );
  const shouldShowBadge =
    !booking?.bookedTrips?.length &&
    (hasRefundedTravelPass || hasExpiredTravelPass);

  usePageTitle('manageBooking');

  // Refetch a clean slate on entry. If the user came back from an abandoned
  // change-booking checkout, `manage.booking` still holds the in-flight
  // exchange state (new trips); refetching from the BE overwrites it with the
  // real, original booking. Fires once, when the booking id first becomes
  // available — tolerating a late hydration (a hard reload of this route
  // rehydrates the persisted booking after mount). The guard ref keeps it to a
  // single fetch, since the refetch itself updates `booking`.
  //
  // Gate the page on this fetch so a refresh can't paint stale persisted data.
  // travelAccountsMap isn't persisted — travel-pass bookings refetch it too.
  const [isLoading, setIsLoading] = useState(true);
  const hasRefetchedRef = useRef(false);
  useEffect(() => {
    if (booking?.id && !hasRefetchedRef.current) {
      hasRefetchedRef.current = true;
      const bookingId = booking.id;

      const refetch = async () => {
        try {
          const refreshed = await dispatch(
            getManageFlowBookingById(bookingId)
          ).unwrap();
          const fulfillments = getBookingNonTripOfferFulfillments(refreshed);

          if (isTravelPassBooking(refreshed) && fulfillments.length) {
            await dispatch(
              getManageFlowTravelAccountsMap(fulfillments)
            ).unwrap();
          }
        } catch {
          // Keep showing the persisted booking if the refetch fails.
        } finally {
          setIsLoading(false);
        }
      };

      refetch();
    } else if (!booking?.id) {
      // No booking to refetch (only reachable before the persisted booking
      // hydrates); nothing to wait for, so never strand on the spinner.
      setIsLoading(false);
    }
  }, [booking?.id, dispatch]);

  const handleGoBack = useCallback(() => {
    dispatch(clearManageFlowBooking());
    navigate(-1);
  }, [dispatch, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-primary">
        <Spinner size={40} />
      </div>
    );
  }

  return (
    <div
      className={cn('pb-16 laptop:pb-4', {
        'pb-36 laptop:pb-20': isRefundButtonVisible,
      })}
    >
      <div className="mx-auto flex max-w-laptop items-center gap-2 p-4">
        <Button
          data-testid="go-back-button"
          variant="icon"
          aria-label={t('text.goBack')}
          onClick={handleGoBack}
          className="w-fit"
        >
          <Icons.arrowLeft />
        </Button>

        <div className="flex items-baseline gap-1">
          <Typography variant={isLaptopOrBigger ? 'heading1-light' : 'body1'}>
            <>
              <TransText i18nKey="booking" />
              &#58;
            </>
          </Typography>
          <Typography variant={isLaptopOrBigger ? 'heading1' : 'subtitle'}>
            {booking?.code}
          </Typography>
        </div>
      </div>
      {shouldShowBadge ? (
        <div className="relative mx-auto max-w-laptop px-4">
          <Divider variant="medium" />
          <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 gap-1 rounded-full border border-neutral bg-white px-2 py-1 laptop:right-8">
            {hasRefundedTravelPass ? (
              <Icons.ticketReturn className="h-3 w-3 text-neutral" />
            ) : (
              <Icons.history className="h-3 w-3 text-neutral" />
            )}
            <Typography variant="label" className="text-neutral">
              <TransText
                i18nKey={hasRefundedTravelPass ? 'refunded' : 'pastBooking'}
              />
            </Typography>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-laptop px-4">
          <Divider variant="medium" />
        </div>
      )}
      <div className="mx-auto mt-8 flex max-w-laptop flex-col px-4 laptop:flex-row laptop:gap-10">
        {booking?.bookedTrips?.length ? (
          <Tickets passengersMap={summaryData.passengersMap} />
        ) : (
          <TravelPasses />
        )}
        {isLaptopOrBigger && (
          <div className="flex flex-col gap-7 pb-2">
            <Typography variant="heading2" as="h1">
              <TransText i18nKey="bookingSummary" />
            </Typography>
            <RegularBookingSummary {...summaryData} />
          </div>
        )}
      </div>
      <Footer
        content={!isLaptopOrBigger && <MobileBookingSummary {...summaryData} />}
        actionButtons={
          <>
            {isRefundButtonVisible &&
              (refundOptions.length > 1 ? (
                <RefundOptions
                  isOpen={isRefundOptionsOpen}
                  onOpenChange={setIsRefundOptionsOpen}
                  options={refundOptions}
                  onOptionClick={handleRefundOptionClick}
                  trigger={
                    <Button
                      size={isLaptopOrBigger ? 'medium' : 'large'}
                      data-testid="refund-button"
                      variant="secondary"
                      className="rounded-lg laptop:w-auto"
                      startAdornment={<Icons.ticketReturn />}
                      endAdornment={
                        <Icons.chevronDown
                          className={cn(
                            'transition-transform duration-200',
                            isRefundOptionsOpen && 'rotate-180'
                          )}
                        />
                      }
                    >
                      <TransText i18nKey="refund" />
                    </Button>
                  }
                />
              ) : (
                <Button
                  size={isLaptopOrBigger ? 'medium' : 'large'}
                  data-testid="refund-button"
                  variant="secondary"
                  className="rounded-lg laptop:w-auto"
                  onClick={handleRefundClick}
                  startAdornment={<Icons.ticketReturn />}
                >
                  <TransText i18nKey="refund" />
                </Button>
              ))}
            {isMakeChangesVisible && (
              <MakeChanges
                isOpen={isMakeChangesOpen}
                onOpenChange={setIsMakeChangesOpen}
                trigger={
                  <Button
                    size={isLaptopOrBigger ? 'medium' : 'large'}
                    data-testid="make-changes-trigger"
                    variant="secondary"
                    className="laptop:w-auto"
                    startAdornment={<Icons.ticketManage />}
                    endAdornment={
                      <Icons.chevronDown
                        className={cn(
                          'transition-transform duration-200',
                          isMakeChangesOpen && 'rotate-180'
                        )}
                      />
                    }
                  >
                    <TransText i18nKey="makeChanges" />
                  </Button>
                }
                onChangeTripsClick={() => setIsChangeTripsModalOpen(true)}
                onChangeTravelClassClick={() => setIsChangeTripsModalOpen(true)}
                onEditPassengerDetailsClick={() =>
                  navigate('/manage-booking/edit-passengers', {
                    state: { fromBookingDetails: true },
                  })
                }
              />
            )}
          </>
        }
      />
      <RefundLegModal
        isOpen={activeRefundModal === 'trips'}
        onClose={handleRefundModalClose}
      />
      <RefundExtrasModal
        isOpen={activeRefundModal === 'extras'}
        onClose={handleRefundModalClose}
      />
      <RefundNonTripModal
        isOpen={activeRefundModal === 'travelPasses'}
        onClose={handleRefundModalClose}
      />
      <ChangeTripsModal
        isOpen={isChangeTripsModalOpen}
        onClose={() => setIsChangeTripsModalOpen(false)}
      />
    </div>
  );
};
